import React, { useState, useEffect, useMemo, useRef } from "react";
import ReactQuill from "react-quill";
import "react-quill/dist/quill.snow.css";
import { Controller, useForm } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import * as yup from "yup";
import moment from "moment";
import { useDispatch, useSelector } from "react-redux";
import { useTranslation } from "src/_common/hooks/useTranslation";
import { EXTERNAL_AE_URL, EXTERNAL_URL, SAGA_ACTIONS, URLS } from "../../../_config";
import { ReactSearchAutocomplete } from "react-search-autocomplete";
import {
  AIJobFormValues,
  Country,
  City,
  JobPostType,
  EducationLevel,
  Language,
  WorkPlace,
  SalaryRange,
  Skill,
} from "src/_common/interfaces/ApiReqRes";
import { useCommonApi } from "../../../_common/hooks/actions/common/appCommonApiHook";
import { useToaster } from "../../../_common/hooks/actions/common/appToasterHook";
import { useJobPostingApi } from "../../../_common/hooks/actions/jobPosting/appJobPostingApiHook";
import { useAppUserDetailsSelector } from "../../../_common/hooks/selectors/userSelector";
import { useHistory } from "react-router-dom";
import CurrencySelectionModal from "../../modal/CurrencySelectionModal";
import SkillSelectionModal from "../../modal/SkillSelectionModal";

// Function to strip HTML tags from text (placed before schema for validation transforms)
const stripHtmlTagsForValidation = (html: string) => {
  if (!html) return "";
  const doc = new DOMParser().parseFromString(html, "text/html");
  const text = doc.body.textContent || "";
  // Trim to remove extra whitespace
  return text.trim();
};

const createValidationSchema = (t: any, isFilterType: boolean = false) =>
  yup.object({
    title: yup.string().required(t("positionTarget.validation.jobTitleRequired")),
    jobDescription: yup
      .string()
      .required(t("positionTarget.validation.jobDescriptionRequired"))
      .test("min-length", t("positionTarget.validation.jobDescriptionMinLength"), function (value) {
        if (!value) return false;
        const plainText = stripHtmlTagsForValidation(value);
        return plainText.length >= 400;
      }),
    jobPostType: yup.string().required(t("positionTarget.validation.jobPostTypeRequired")),
    workplace: yup.string().required(t("positionTarget.validation.workplaceRequired")),
    educationLevel: yup.string(),
    country: yup.string().required(t("positionTarget.validation.countryRequired")),
    jobLocation: yup.string().required(t("positionTarget.validation.cityRequired")),
    selectedLanguages: yup.array().of(yup.string()).min(1, t("positionTarget.validation.languagesRequired")),
    salary: isFilterType
      ? yup.string()
      : yup
          .string()
          .required(t("positionTarget.validation.salaryRequired"))
          .matches(/^\d{3,9}$/, t("positionTarget.validation.salaryDigitsValidation")),
    salaryPeriod: isFilterType
      ? yup.string()
      : yup.string().required(t("positionTarget.validation.salaryPeriodRequired")),
    vacancies: isFilterType
      ? yup.number()
      : yup
          .number()
          .required(t("positionTarget.validation.vacanciesRequired"))
          .min(1, t("positionTarget.validation.vacanciesMinimum")),
  });

interface PositionTargetProps {
  jobId?: string;
  currentJob?: any; // Job details passed from parent
}

export default function PositionTarget({ jobId, currentJob }: PositionTargetProps) {
  const { t, loading: translationsLoading } = useTranslation("job-details");
  const history = useHistory();
  const dispatch = useDispatch();
  const commonApi = useCommonApi();
  const jobPostingApi = useJobPostingApi();
  const toast = useToaster();
  const userDetails = useAppUserDetailsSelector();
  const [selectedCountryId, setSelectedCountryId] = useState<string>("");
  const [selectedCityId, setSelectedCityId] = useState<string>("");
  const [selectedCountryName, setSelectedCountryName] = useState<string>("");
  const [selectedCityName, setSelectedCityName] = useState<string>("");
  // Helper to determine if city dropdown should be disabled
  const isCityDisabled = !selectedCountryId;
  const [countries, setCountries] = useState<Array<{ id: string; name: string }>>([]);
  const [cities, setCities] = useState<Array<{ id: string; name: string }>>([]);
  const [hasCountryInteracted, setHasCountryInteracted] = useState(false);
  const [hasCityInteracted, setHasCityInteracted] = useState(false);
  const [isEnhancing, setIsEnhancing] = useState(false);
  const [enhancingField, setEnhancingField] = useState<"jobDescription" | "roleDescription" | "rejectionSettings">(
    "jobDescription",
  );
  const [isGeneratingJobDescription, setIsGeneratingJobDescription] = useState(false);
  const [selectedLanguages, setSelectedLanguages] = useState<string[]>(["English"]);
  const [showLanguageDropdown, setShowLanguageDropdown] = useState(false);
  const [selectedSkills, setSelectedSkills] = useState<string[]>([]);
  const [selectedSkillIds, setSelectedSkillIds] = useState<number[]>([]); // Store skill IDs from API response
  const [aiGeneratedSkills, setAiGeneratedSkills] = useState<string[]>([]); // Skills with is_matched: 0
  const [showAiSkillsSection, setShowAiSkillsSection] = useState(false); // Control AI skills section visibility
  const [customSkills, setCustomSkills] = useState<string[]>([]);
  const [showSkillModal, setShowSkillModal] = useState(false);
  // Keys to force remounting autocomplete inputs to close dropdowns on select
  const [countrySelectKey, setCountrySelectKey] = useState(0);
  // Salary and currency related states
  const [selectedCurrency, setSelectedCurrency] = useState<any | null>("USD");
  const [selectedCurrencySymbol, setSelectedCurrencySymbol] = useState<string>("$");
  const [hideSalary, setHideSalary] = useState(false);
  const [showCurrencyModal, setShowCurrencyModal] = useState(false);
  const [citySelectKey, setCitySelectKey] = useState(0);
  const [showAiLanguageDropdown, setShowAiLanguageDropdown] = useState(false);
  const [selectedAiLanguage, setSelectedAiLanguage] = useState<string>("EN");
  const [expiryDate, setExpiryDate] = useState<string>("");
  const [selectedGenderId, setSelectedGenderId] = useState<number | null>(null);
  const [selectedNationalities, setSelectedNationalities] = useState<string[]>([]);
  const [selectedNationalityIds, setSelectedNationalityIds] = useState<number[]>([]);
  const [nationalitySearchItems, setNationalitySearchItems] = useState<{ id: string; name: string }[]>([]);
  const [hasNationalityInteracted, setHasNationalityInteracted] = useState(false);
  const [nationalitySelectKey, setNationalitySelectKey] = useState(0);
  const [selectedIndustryId, setSelectedIndustryId] = useState<number | null>(null);
  const [selectedIndustryName, setSelectedIndustryName] = useState<string>("");
  const [hasIndustryInteracted, setHasIndustryInteracted] = useState(false);
  const [industrySelectKey, setIndustrySelectKey] = useState(0);
  const [industryItems, setIndustryItems] = useState<{ id: string; name: string }[]>([]);
  const nationalityCurrentSearchRef = useRef<string>("");
  const [nationalityInputSearch, setNationalityInputSearch] = useState<string>("");
  const industryCurrentSearchRef = useRef<string>("");
  const [industryInputSearch, setIndustryInputSearch] = useState<string>("");

  // Redux selectors
  const {
    jobPostTypes,
    workplaces,
    educationLevels,
    countriesNew,
    citiesNew,
    languages,
    salaryRanges,
    currencies,
    skills,
    nationalities,
    industries,
  } = useSelector((state: any) => ({
    jobPostTypes: state.global.jobPostTypes,
    workplaces: state.global.workplaces,
    educationLevels: state.global.educationLevels,
    countriesNew: state.global.countriesNew,
    citiesNew: state.global.citiesNew,
    languages: state.global.languages,
    salaryRanges: state.global.salaryRanges,
    currencies: state.global.currencies,
    skills: state.global.skills,
    nationalities: state.global.nationalities,
    industries: state.global.industries,
  }));

  // Use fallback data if no data from API
  const jobPostTypesData = jobPostTypes && jobPostTypes.length > 0 ? jobPostTypes : null;
  const workplacesData = workplaces && workplaces.length > 0 ? workplaces : null;
  const educationLevelsData = educationLevels && educationLevels.length > 0 ? educationLevels : null;
  const countriesData = countriesNew && countriesNew.length > 0 ? countriesNew : null;
  const citiesData = citiesNew && citiesNew.length > 0 ? citiesNew : null;
  const languagesData = languages && languages.length > 0 ? languages : null;
  const salaryRangesData = salaryRanges && salaryRanges.length > 0 ? salaryRanges : null;
  const currenciesData = currencies && currencies.length > 0 ? currencies : null;
  const skillsData = skills && skills.length > 0 ? skills : null;
  const nationalitiesData = nationalities?.length > 0 ? nationalities : null;
  const industriesData = industries?.length > 0 ? industries : null;
  const jobToEdit = currentJob; // Use currentJob prop passed from parent
  const isFilterType = jobToEdit?.status === 10; // Check if job is filter type (AI filter)
  const isLive = !!jobToEdit?.master_job_id;
  const [isEditing, setIsEditing] = useState<boolean>(!currentJob); // new jobs start in edit mode
  const isReadOnly = !isEditing;

  const {
    control,
    handleSubmit,
    watch,
    formState: { errors },
    setValue,
    trigger,
  } = useForm<AIJobFormValues>({
    resolver: yupResolver(createValidationSchema(t, isFilterType)),
    defaultValues: {
      title: "",
      jobDescription: "",
      jobPostType: "",
      workplace: "",
      educationLevel: "",
      country: "",
      jobLocation: "",
      selectedLanguages: ["English"],
      salary: "",
      salaryPeriod: "monthly",
      vacancies: 1,
      roleDescription: "",
      skills: [],
      rejectionSettings: "",
    },
  });

  // Memoize placeholder text to ensure it updates when translation loads
  const jobDescriptionPlaceholder = useMemo(() => {
    if (translationsLoading) {
      return "Enter job description..."; // Fallback text while loading
    }
    const translatedText = t("positionTarget.form.jobDescription.placeholder");
    console.log("Job Description Placeholder:", translatedText);
    return translatedText;
  }, [t, translationsLoading]);

  // Language handler functions
  const handleLanguageAdd = (language: Language) => {
    if (!selectedLanguages.includes(language.name)) {
      const newLanguages = [...selectedLanguages, language.name];
      setSelectedLanguages(newLanguages);
      setValue("selectedLanguages", newLanguages);
    }
    setShowLanguageDropdown(false);
  };

  const handleLanguageRemove = (languageToRemove: string) => {
    // Prevent removing if only 1 language left
    if (selectedLanguages.length <= 1) {
      return;
    }
    const newLanguages = selectedLanguages.filter((lang) => lang !== languageToRemove);
    setSelectedLanguages(newLanguages);
    setValue("selectedLanguages", newLanguages);
  };

  // const availableLanguages = languagesData?.filter((lang: Language) => !selectedLanguages.includes(lang.name)) || [];
  const availableLanguages = languagesData?.filter((lang: Language) => !selectedLanguages.includes(lang.name)) || [];

  // Skill handler functions
  const handleSkillRemove = (skillToRemove: string) => {
    // Find the index of the skill to get its corresponding ID
    const skillIndex = selectedSkills.indexOf(skillToRemove);
    const newSkills = selectedSkills.filter((skill) => skill !== skillToRemove);

    // Remove corresponding skill ID if index exists
    let newSkillIds = [...selectedSkillIds];
    if (skillIndex !== -1 && skillIndex < selectedSkillIds.length) {
      newSkillIds = selectedSkillIds.filter((_, index) => index !== skillIndex);
    }

    setSelectedSkills(newSkills);
    setSelectedSkillIds(newSkillIds);
    setValue("skills", newSkills);
  };

  const handleSkillAdd = (skill: Skill) => {
    if (!selectedSkills.includes(skill.name)) {
      const newSkills = [...selectedSkills, skill.name];
      const newSkillIds = [...selectedSkillIds, skill.id];
      setSelectedSkills(newSkills);
      setSelectedSkillIds(newSkillIds);
      setValue("skills", newSkills);
    }
  };

  const handleAiSkillAdd = (skillName: string) => {
    if (!selectedSkills.includes(skillName)) {
      const newSkills = [...selectedSkills, skillName];
      setSelectedSkills(newSkills);
      setValue("skills", newSkills);

      // Remove from AI generated skills
      setAiGeneratedSkills((prev) => prev.filter((skill) => skill !== skillName));
    }
  };

  const handleAiSkillRemove = (skillName: string) => {
    // Add back to AI generated skills
    if (!aiGeneratedSkills.includes(skillName)) {
      setAiGeneratedSkills((prev) => [...prev, skillName]);
    }
  };

  // Fetch countries from API
  const getCountries = (searchName: string = "") => {
    const params: any = { limit: 30 };
    if (searchName.trim()) {
      params.name = searchName.trim();
    }
    dispatch({
      type: SAGA_ACTIONS.COMMON.COUNTRIES_NEW,
      payload: params,
    });
  };

  // Fetch cities from API based on country (mimic completeProfile)
  const getCities = (countryId: string, searchName: string = "") => {
    if (!countryId) {
      setCities([]);
      return;
    }
    const params: any = { country_id: countryId, limit: 200 };
    if (searchName.trim()) {
      params.name = searchName.trim();
    }
    dispatch({
      type: SAGA_ACTIONS.COMMON.CITIES_NEW,
      payload: params,
    });
  };

  // Handle country selection (mimic completeProfile)
  const handleCountrySelect = (item: any, setValue: Function) => {
    setValue("country", item.name);
    setSelectedCountryId(item.id);
    setSelectedCountryName(item.name);
    setValue("jobLocation", ""); // Clear city when country changes
    setSelectedCityId(""); // Clear selected city ID when country changes
    setSelectedCityName("");
    setCities([]);
    // Reset interaction and remount to close dropdown
    setHasCountryInteracted(false);
    setCountrySelectKey((k) => k + 1);
    getCities(item.id); // Load cities for selected country

    // Set currency based on selected country
    if (item.currency_code) {
      setSelectedCurrency(item.currency_code);

      // Set currency symbol based on currency code
      const currencySymbolMap: { [key: string]: string } = {
        AED: "د.إ",
        AUD: "A$",
        BDT: "৳",
        BGN: "лв",
        BND: "B$",
        BRL: "R$",
        CAD: "C$",
        CHF: "CHF",
        CNY: "¥",
        CZK: "Kč",
        DKK: "kr",
        EUR: "€",
        GBP: "£",
        HKD: "HK$",
        HUF: "Ft",
        IDR: "Rp",
        ILS: "₪",
        INR: "₹",
        JPY: "¥",
        KRW: "₩",
        KWD: "د.ك",
        MXN: "Mex$",
        MYR: "RM",
        NOK: "kr",
        NZD: "NZ$",
        OMR: "ر.ع.",
        PHP: "₱",
        PKR: "₨",
        PLN: "zł",
        QAR: "ر.ق",
        RON: "lei",
        RUB: "₽",
        SAR: "ر.س",
        SEK: "kr",
        SGD: "S$",
        THB: "฿",
        TRY: "₺",
        TWD: "NT$",
        USD: "$",
        VND: "₫",
        XCD: "EC$",
        ZAR: "R",
      };

      const symbol = currencySymbolMap[item.currency_code] || "$";
      setSelectedCurrencySymbol(symbol);
    }
  };

  // Handle city selection (mimic completeProfile)
  const handleCitySelect = (item: any, setValue: Function) => {
    setValue("jobLocation", item.name);
    setSelectedCityId(item.id);
    setSelectedCityName(item.name);
    // Reset interaction and remount to close dropdown
    setHasCityInteracted(false);
    setCitySelectKey((k) => k + 1);
  };

  // Add onSubmit handler
  const onSubmit = (data: AIJobFormValues) => {
    console.log("Form submitted with data:", data);
    console.log("Form errors:", errors);

    if (!jobId) {
      toast.error(t("positionTarget.messages.jobIdRequired"));
      return;
    }

    // Validate that country and city are actually selected
    if (!selectedCountryId) {
      toast.error(t("positionTarget.validation.selectValidCountry"));
      return;
    }

    if (!selectedCityId) {
      toast.error(t("positionTarget.validation.selectValidCity"));
      return;
    }

    // Get selected country and city details from our formatted arrays and original Redux data
    const selectedCountryFormatted = countries.find((country) => country.id === selectedCountryId);
    const selectedCityFormatted = cities.find((city) => city.id === selectedCityId);

    // Get full country and city data from Redux for additional fields
    const selectedCountry = countriesData?.find((country: Country) => country.id.toString() === selectedCountryId);
    const selectedCity = citiesData?.find((city: City) => city.id.toString() === selectedCityId);

    // Get selected job post type and education level details
    const selectedJobPostType = jobPostTypesData?.find((jobType: any) => jobType.name === data.jobPostType);
    const selectedWorkplace = workplacesData?.find((workplace: any) => workplace.name === data.workplace);
    const selectedEducationLevel = educationLevelsData?.find(
      (level: EducationLevel) => level.name == data.educationLevel,
    );

    // Get the original job data to preserve existing language flags

    // Use original language flags from jobToEdit, fallback to form data if not available
    const arFlag = jobToEdit ? jobToEdit.ar_flag : selectedLanguages?.includes("Arabic") ? 1 : 0;
    const enFlag = jobToEdit ? jobToEdit.en_flag : selectedLanguages?.includes("English") ? 1 : 0;
    console.log(countriesData, selectedCountry, selectedCity);
    // Transform form data to API payload for Position Target fields only
    const apiPayload = {
      job: {
        // Required fields from error message
        job_source: 1, // hard coded
        status: 1, // hard coded
        company_name: userDetails?.company?.name || "", // company name

        // Position Target specific fields
        job_title: data.title,
        job_desc: data.jobDescription,
        job_type: data.jobPostType,
        job_type_id: selectedJobPostType?.id || 1,
        workplace_type: data.workplace,
        workplace_type_id: selectedWorkplace?.id || 1,
        ...(data.educationLevel && {
          education_level: data.educationLevel,
          education_level_id: selectedEducationLevel?.id || undefined,
        }),

        // Country fields with all required variations
        country_id: selectedCountryFormatted
          ? parseInt(selectedCountryFormatted.id)
          : selectedCountry?.id || selectedCountryId,
        country_name: selectedCountry?.name || selectedCountryFormatted?.name || data.country,
        country_slug: selectedCountry?.slug || data.country?.toLowerCase().replace(/\s+/g, "-"),
        country_name_ar: selectedCountry?.name_ar || selectedCountryFormatted?.name || data.country,
        country_slug_ar: selectedCountry?.slug_ar || data.country?.toLowerCase().replace(/\s+/g, "-") + "-ar",

        // City fields with all required variations
        city_id: selectedCityFormatted ? parseInt(selectedCityFormatted.id) : selectedCity?.id || 0,
        city_name: selectedCity?.name || selectedCityFormatted?.name || data.jobLocation,
        city_slug: selectedCity?.slug || data.jobLocation?.toLowerCase().replace(/\s+/g, "-"),
        city_name_ar: selectedCity?.name_ar || selectedCityFormatted?.name || data.jobLocation,
        city_slug_ar: selectedCity?.slug_ar || data.jobLocation?.toLowerCase().replace(/\s+/g, "-") + "-ar",

        // Language flags
        en_flag: enFlag,
        ar_flag: arFlag,

        // Salary and vacancies
        salary_range: data.salary,
        salary_period: data.salaryPeriod,
        currency_name: selectedCurrency || "USD",
        hide_salary: hideSalary ? 1 : 0,
        no_of_vacancy: data.vacancies,

        gender_id: selectedGenderId ?? null,
        job_nationality_id: selectedNationalityIds,
        industry_ids: selectedIndustryId ? [selectedIndustryId] : [],

        // Include required fields that shouldn't be changed
        company_id: userDetails?.company?.user_id || 0,
        created_by_id: userDetails?.company?.user_id || 0,
      },
    };

    // console.log("Selected Country ID:", selectedCountryId);
    // console.log("Selected City ID:", selectedCityId);
    // console.log("Selected Country Formatted:", selectedCountryFormatted);
    // console.log("Selected City Formatted:", selectedCityFormatted);
    // console.log("Updating job with payload:", apiPayload);

    // Call the job update API
    jobPostingApi.callUpdateJob(
      { ...apiPayload, jobId },
      (message: string, response: any) => {
        // Success callback
        console.log("Job updated successfully:", response);
        toast.success(message || t("positionTarget.messages.jobUpdatedSuccess"));
        setIsEditing(false);
        // history.push(URLS.DASHBOARD.MAIN);
      },
      (errorMessage: string) => {
        // Error callback
        console.error("Failed to update job:", errorMessage);
        toast.error(errorMessage || t("positionTarget.messages.jobUpdateError"));
      },
    );
  };

  // Fetch required data on component mount
  useEffect(() => {
    dispatch({
      type: SAGA_ACTIONS.COMMON.JOB_POST_TYPES,
      payload: { limit: 30, offset: 0 },
    });

    dispatch({
      type: SAGA_ACTIONS.COMMON.WORK_PLACES,
      payload: { limit: 30, offset: 0 },
    });

    dispatch({
      type: SAGA_ACTIONS.COMMON.EDUCATION_LEVELS,
      payload: { limit: 30, offset: 0 },
    });

    dispatch({
      type: SAGA_ACTIONS.COMMON.COUNTRIES_NEW,
      payload: { limit: 300, offset: 0 },
    });

    dispatch({
      type: SAGA_ACTIONS.COMMON.LANGUAGES,
      payload: { limit: 30, offset: 0 },
    });

    // Nationality and industries now load on demand
  }, [dispatch]);

  // Sync selectedLanguages with form
  useEffect(() => {
    setValue("selectedLanguages", selectedLanguages);
  }, [selectedLanguages, setValue]);

  // Close language dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      const target = event.target as HTMLElement;
      if (!target.closest(".language-dropdown-container")) {
        setShowLanguageDropdown(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [showLanguageDropdown]);

  // Function to strip HTML tags from text
  const stripHtmlTags = (html: string) => {
    const doc = new DOMParser().parseFromString(html, "text/html");
    return doc.body.textContent || "";
  };

  useEffect(() => {
    if (jobId && currentJob) {
      const jobToEdit = currentJob;
      if (jobToEdit) {
        console.log("Pre-filling form for editing job:", jobToEdit);
        // Set form values from the job data (same pattern as NewJobPostingMain2)
        setValue("title", jobToEdit.job_title || "");

        // Preserve HTML in job description
        setValue("jobDescription", jobToEdit.job_desc || "");

        setValue("jobPostType", jobToEdit.job_type || "");
        setValue("workplace", jobToEdit.workplace_type || "");
        setValue("educationLevel", jobToEdit.education_level || "");
        setValue("country", jobToEdit.country_name || "");
        setValue("jobLocation", jobToEdit.city_name || "");

        // Set salary and salary period
        setValue("salary", jobToEdit.salary_range);
        setValue("salaryPeriod", jobToEdit.salary_period || "");
        setValue("vacancies", jobToEdit.no_of_vacancy || 1);

        // Set expiry date
        if (jobToEdit.expiry_date) {
          setExpiryDate(jobToEdit.expiry_date);
        }

        // Set currency and hide salary flag
        if (jobToEdit.currency_name) {
          setSelectedCurrency(jobToEdit.currency_name);

          // Set currency symbol based on currency name
          const currencySymbolMap: { [key: string]: string } = {
            AED: "د.إ",
            AUD: "A$",
            BDT: "৳",
            BGN: "лв",
            BND: "B$",
            BRL: "R$",
            CAD: "C$",
            CHF: "CHF",
            CNY: "¥",
            CZK: "Kč",
            DKK: "kr",
            EUR: "€",
            GBP: "£",
            HKD: "HK$",
            HUF: "Ft",
            IDR: "Rp",
            ILS: "₪",
            INR: "₹",
            JPY: "¥",
            KRW: "₩",
            KWD: "د.ك",
            MXN: "Mex$",
            MYR: "RM",
            NOK: "kr",
            NZD: "NZ$",
            OMR: "ر.ع.",
            PHP: "₱",
            PKR: "₨",
            PLN: "zł",
            QAR: "ر.ق",
            RON: "lei",
            RUB: "₽",
            SAR: "ر.س",
            SEK: "kr",
            SGD: "S$",
            THB: "฿",
            TRY: "₺",
            TWD: "NT$",
            USD: "$",
            VND: "₫",
            XCD: "EC$",
            ZAR: "R",
          };

          const symbol = currencySymbolMap[jobToEdit.currency_name] || "$";
          setSelectedCurrencySymbol(symbol);
        }

        if (jobToEdit.hide_salary !== undefined) {
          setHideSalary(jobToEdit.hide_salary === 1);
        }

        // Set language flags based on en_flag and ar_flag (same as NewJobPostingMain2)
        const languages = [];
        if (jobToEdit.en_flag == 1) languages.push("English");
        if (jobToEdit.ar_flag == 1) languages.push("Arabic");
        if (languages.length > 0) {
          setValue("selectedLanguages", languages);
          setSelectedLanguages(languages);
        }

        // Set skills from skill_list
        if (jobToEdit.skill_list) {
          const skillsArray = jobToEdit.skill_list.split(",").map((skill: string) => skill.trim());
          setSelectedSkills(skillsArray);
          setValue("skills", skillsArray);

          // Set skill IDs if available
          if (jobToEdit.job_skills_id && Array.isArray(jobToEdit.job_skills_id)) {
            setSelectedSkillIds(jobToEdit.job_skills_id);
          }
        }

        // Set country and city IDs for editing mode
        if (jobToEdit.country_id) {
          setSelectedCountryId(jobToEdit.country_id.toString());
          setSelectedCountryName(jobToEdit.country_name || "");

          // Fetch cities for the selected country
          dispatch({
            type: SAGA_ACTIONS.COMMON.CITIES_NEW,
            payload: { limit: 300, offset: 0, country_id: jobToEdit.country_id },
          });
        }

        // Set city ID and name after setting country
        if (jobToEdit.city_id) {
          setSelectedCityId(jobToEdit.city_id.toString());
          setSelectedCityName(jobToEdit.city_name || "");
        }

        if (jobToEdit.gender_id) setSelectedGenderId(jobToEdit.gender_id);
        if (jobToEdit.nationality_list && Array.isArray(jobToEdit.job_nationality_id) && jobToEdit.job_nationality_id.length) {
          const names = jobToEdit.nationality_list.split(",").map((n: string) => n.trim()).filter(Boolean);
          setSelectedNationalities(names);
          setSelectedNationalityIds(jobToEdit.job_nationality_id);
        }
        if (jobToEdit.industry_list && Array.isArray(jobToEdit.industry_ids) && jobToEdit.industry_ids.length) {
          setSelectedIndustryId(jobToEdit.industry_ids[0]);
          setSelectedIndustryName(jobToEdit.industry_list.split(",")[0].trim());
        }
      }
    }
  }, [jobId, currentJob, setValue, dispatch, industriesData]);

  // Populate countries from Redux state
  useEffect(() => {
    if (countriesNew && countriesNew.length > 0) {
      const formattedCountries = countriesNew.map((country: any) => ({
        id: country.id.toString(),
        name: country.name,
      }));
      setCountries(formattedCountries);
    }
  }, [countriesNew]);

  // Populate cities from Redux state
  useEffect(() => {
    if (citiesNew && citiesNew.length > 0) {
      const formattedCities = citiesNew.map((city: any) => ({
        id: city.id.toString(),
        name: city.name,
      }));
      setCities(formattedCities);
    }
  }, [citiesNew]);

  // Load countries on component mount
  useEffect(() => {
    getCountries();
  }, []);

  useEffect(() => {
    if (nationalitiesData && nationalitiesData.length > 0) {
      setNationalitySearchItems(nationalitiesData.map((n: any) => ({ id: n.id.toString(), name: n.name })));
      if (nationalityCurrentSearchRef.current) {
        setNationalityInputSearch(nationalityCurrentSearchRef.current);
      }
    }
  }, [nationalitiesData]);

  useEffect(() => {
    if (industriesData && industriesData.length > 0) {
      setIndustryItems(industriesData.map((i: any) => ({ id: i.id.toString(), name: i.name })));
      if (industryCurrentSearchRef.current) {
        setIndustryInputSearch(industryCurrentSearchRef.current);
      }
    }
  }, [industriesData]);

  const nationalityDebounceRef = useRef<NodeJS.Timeout | null>(null);
  const industryDebounceRef = useRef<NodeJS.Timeout | null>(null);

  const getNationalitiesSearch = (q: string = "") => {
    const params: any = { limit: 50 };
    if (q.trim()) params.q = q.trim();
    dispatch({ type: SAGA_ACTIONS.COMMON.NATIONALITIES, payload: params });
  };

  const loadIndustries = (q: string = "") => {
    const params: any = { limit: 100, offset: 0 };
    if (q.trim()) params.q = q.trim();
    dispatch({ type: SAGA_ACTIONS.COMMON.INDUSTRIES, payload: params });
  };

  // Close currency modal when pressing Escape key
  useEffect(() => {
    const handleEscapeKey = (event: KeyboardEvent) => {
      if (event.key === "Escape") {
        setShowCurrencyModal(false);
      }
    };

    if (showCurrencyModal) {
      document.addEventListener("keydown", handleEscapeKey);
      return () => document.removeEventListener("keydown", handleEscapeKey);
    }
  }, [showCurrencyModal]);

  // Effect to set proper values for ReactSearchAutocomplete in edit mode (mimic completeProfile)
  useEffect(() => {
    if (jobId && currentJob) {
      const jobToEdit = currentJob;
      if (jobToEdit) {
        // Set country/city IDs and names from jobToEdit
        if (jobToEdit.country_id) {
          setSelectedCountryId(jobToEdit.country_id.toString());
          setSelectedCountryName(jobToEdit.country_name || "");
          setValue("country", jobToEdit.country_name || "");
          // Fetch cities for the selected country
          getCities(jobToEdit.country_id.toString());
        }
        if (jobToEdit.city_id) {
          setSelectedCityId(jobToEdit.city_id.toString());
          setSelectedCityName(jobToEdit.city_name || "");
          setValue("jobLocation", jobToEdit.city_name || "");
        }
      }
    }
  }, [jobId, currentJob, setValue]);

  // Close AI language dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      const target = event.target as HTMLElement;
      if (!target.closest(".ai-language-dropdown-container")) {
        setShowAiLanguageDropdown(false);
      }
    };

    if (showAiLanguageDropdown) {
      document.addEventListener("mousedown", handleClickOutside);
      return () => document.removeEventListener("mousedown", handleClickOutside);
    }
  }, [showAiLanguageDropdown]);

  // Handle opening AI language dropdown
  const handleOpenAiLanguageDropdown = () => {
    // Get the current job description value
    const currentValue = watch("jobDescription");

    // Check if there's content to enhance
    if (!currentValue || !currentValue.trim()) {
      toast.error(t("positionTarget.messages.enterJobDescFirst"));
      return;
    }

    // Strip HTML and check minimum length
    const plainText = stripHtmlTags(currentValue);
    if (plainText.trim().length < 10) {
      toast.error(t("positionTarget.validation.minCharacters"));
      return;
    }

    // Show language dropdown
    setShowAiLanguageDropdown(true);
  };

  // Handle language selection and call AI API
  const handleAiLanguageSelect = (languageCode: string) => {
    setSelectedAiLanguage(languageCode);
    setShowAiLanguageDropdown(false);

    // Get the current job description value
    const currentValue = watch("jobDescription");
    const plainText = stripHtmlTags(currentValue);

    // Directly call the API to enhance the text
    setIsEnhancing(true);
    setEnhancingField("jobDescription");

    const requestData = {
      text: plainText,
      text_type: "job_description",
      language: languageCode,
    };

    commonApi.callEnhanceText(
      requestData,
      (message: string, response: any) => {
        // Success callback
        if (response && response.enhanced_text) {
          setValue("jobDescription", response.enhanced_text, {
            shouldDirty: true,
            shouldValidate: true,
          });

          toast.success(t("positionTarget.messages.jobDescEnhanced"));
        } else {
          toast.error(t("positionTarget.messages.enhanceError"));
        }
        setIsEnhancing(false);
      },
      (errorMessage: string) => {
        // Error callback
        toast.error(errorMessage || t("positionTarget.messages.enhanceError"));
        setIsEnhancing(false);
      },
    );
  };

  // Handle opening AI modal for job description (deprecated - now using language dropdown)
  const handleOpenAiModal = () => {
    setEnhancingField("jobDescription");

    // Get the current job description value
    const currentValue = watch("jobDescription");

    // Check if there's content to enhance
    if (!currentValue || !currentValue.trim()) {
      toast.error(t("positionTarget.messages.enterJobDescFirst"));
      return;
    }

    // Strip HTML and check minimum length
    const plainText = stripHtmlTags(currentValue);
    if (plainText.trim().length < 10) {
      toast.error(t("positionTarget.validation.minCharacters"));
      return;
    }

    // Directly call the API to enhance the text
    setIsEnhancing(true);

    const requestData = {
      text: plainText,
      text_type: "job_description",
    };

    commonApi.callEnhanceText(
      requestData,
      (message: string, response: any) => {
        // Success callback
        if (response && response.enhanced_text) {
          setValue("jobDescription", response.enhanced_text, {
            shouldDirty: true,
            shouldValidate: true,
          });

          toast.success(t("positionTarget.messages.jobDescEnhanced"));
        } else {
          toast.error(t("positionTarget.messages.enhanceError"));
        }
        setIsEnhancing(false);
      },
      (errorMessage: string) => {
        // Error callback
        toast.error(errorMessage || t("positionTarget.messages.enhanceError"));
        setIsEnhancing(false);
      },
    );
  };

  // Handle AI generation for job description
  const handleAiGenerate = () => {
    const jobDescriptionValue = watch("jobDescription");

    if (!jobDescriptionValue || !jobDescriptionValue.trim()) {
      toast.error(t("positionTarget.messages.enterJobDescFirst"));
      return;
    }

    setIsGeneratingJobDescription(true);

    const requestData: any = {
      text: jobDescriptionValue,
    };
    if (selectedGenderId) {
      requestData.gender = selectedGenderId === 1 ? "Male" : "Female";
    }
    if (selectedNationalities.length > 0) {
      requestData.nationality = selectedNationalities.join(",");
    }
    if (selectedIndustryName) {
      requestData.industry = selectedIndustryName;
    }

    commonApi.callGenerateJobDescriptionSkills(
      requestData,
      (message: string, response: any) => {
        // Success callback
        if (response) {
          // Update job description if provided
          if (response.role_description_and_responsibilites) {
            const cleanText = stripHtmlTags(response.role_description_and_responsibilites);
            setValue("jobDescription", cleanText, {
              shouldDirty: true,
              shouldValidate: true,
            });
          }

          toast.success(t("positionTarget.messages.jobDescGenerated"));
        }
        setIsGeneratingJobDescription(false);
      },
      (errorMessage: string) => {
        // Error callback
        toast.error(errorMessage || t("positionTarget.messages.generateError"));
        setIsGeneratingJobDescription(false);
      },
    );
  };

  const handleNationalitySelect = (item: any) => {
    const id = parseInt(item.id);
    if (!selectedNationalityIds.includes(id)) {
      setSelectedNationalities((prev) => [...prev, item.name]);
      setSelectedNationalityIds((prev) => [...prev, id]);
    }
    nationalityCurrentSearchRef.current = "";
    setNationalityInputSearch("");
    setNationalitySelectKey((k) => k + 1);
  };

  const handleNationalityRemove = (name: string, id: number) => {
    setSelectedNationalities((prev) => prev.filter((n) => n !== name));
    setSelectedNationalityIds((prev) => prev.filter((i) => i !== id));
  };

  const handleIndustrySelect = (item: any) => {
    setSelectedIndustryId(parseInt(item.id));
    setSelectedIndustryName(item.name);
    setHasIndustryInteracted(false);
    setIndustryInputSearch(item.name);
    setIndustrySelectKey((k) => k + 1);
  };

  return (
    <div className="ai-job-description-main position-target-wrapper">
      <div className="ai-job-container">
        <form className="ai-job-form" onSubmit={handleSubmit(onSubmit)}>
          <div className="ai-job-left">
            <div className="ai-job-header">
              <div className="header-left-ai-job">
                <div className="job-overview-section">
                  <h1 className="job-overview-title">{jobToEdit?.job_title}</h1>
                  <p className="job-overview-subtitle">{t("positionTarget.subtitle")}</p>
                </div>
              </div>
              {/* Edit button — only when job exists, not live, not in edit mode */}
              {currentJob && !isLive && !isEditing && !isFilterType && (
                <div className="form-actions">
                  <button type="button" className="btn-preview" onClick={() => setIsEditing(true)}>
                    <i className="fa fa-pencil mr-2"></i> Edit
                  </button>
                </div>
              )}

              {/* Save & Publish button — only while editing */}
              {isEditing && !isFilterType && (
                <div className="form-actions">
                  <button type="submit" className="btn-preview">
                    {t("positionTarget.savePublish")}
                  </button>
                </div>
              )}

              {/* Live badge — when job is live */}
              {/* {isLive && (
                <div className="form-actions">
                  <span className="status-badge hired">
                    <i className="fa fa-lock mr-2"></i> Live
                  </span>
                </div>
              )} */}
            </div>

            <div className="form-section">
              <div className="d-flex justify-content-between wrap-info-container">
                <div className="form-section-title-wrap">
                  <h3 className="section-title">{t("positionTarget.information.title")}</h3>
                  <p className="section-subtitle">{t("positionTarget.information.subtitle")}</p>
                </div>
                <div className="wrap-lang-exp-grp" style={{ alignItems: "flex-end" }}>
                  {/* Expiry Date (Edit Mode Only) */}
                  {jobId && expiryDate && (
                    <div className="form-group">
                      <label className="form-label">{t("positionTarget.expiresOn")}</label>
                      <div className="expiry-date-display">
                        <i className="fa fa-calendar" aria-hidden="true"></i>
                        <span>{moment(expiryDate).format("DD MMM YYYY")}</span>
                      </div>
                    </div>
                  )}

                  <div className="d-flex align-items-center" style={{ gap: "10px" }}>
                    {jobToEdit?.master_job_id && jobToEdit.status !== 10 && (
                      <button
                        type="button"
                        className="status-badge hired view-job"
                        onClick={(e) => {
                          if (jobToEdit.master_job_id) {
                            const baseUrl = jobToEdit.country_name === "UAE" ? EXTERNAL_AE_URL : EXTERNAL_URL;
                            const viewUrl = `${baseUrl}job/view/${jobToEdit.master_job_id}`;
                            window.open(viewUrl, "_blank");
                          }
                        }}
                      >
                        <i className="fa fa-external-link-alt mr-2"></i> {t("positionTarget.viewJob")}
                      </button>
                    )}
                    <div className="language-section">
                      {/* <label className="language-label">{t("positionTarget.information.language")}</label> */}
                      <div
                        className={`language-tags ${errors.selectedLanguages ? "error" : ""}`}
                        style={{
                          background: "#a5c5e5",
                          borderRadius: "30px",
                          gap: "0",
                          height: "45px",
                        }}
                      >
                        <i className="fa fa-globe"></i>
                        {selectedLanguages.map((language, index) => (
                          <span key={index} className="language-tag">
                            {language}
                            {!isReadOnly && selectedLanguages.length > 1 && (
                              <button
                                type="button"
                                className="tag-remove"
                                onClick={() => handleLanguageRemove(language)}
                              >
                                ×
                              </button>
                            )}
                          </span>
                        ))}
                        {/* <div className="language-dropdown-container">
                        <button
                          type="button"
                          className={`language-dropdown ${showLanguageDropdown ? "active" : ""}`}
                          onClick={(e) => {
                            e.preventDefault();
                            e.stopPropagation();
                            setShowLanguageDropdown(!showLanguageDropdown);
                          }}
                        ></button>
                        {showLanguageDropdown && (
                          <div className="language-dropdown-menu">
                            {availableLanguages.length > 0
                              ? availableLanguages.map((language: Language) => (
                                  <div
                                    key={language.id}
                                    className="language-dropdown-item"
                                    onClick={() => handleLanguageAdd(language)}
                                  >
                                    {language.name}
                                  </div>
                                ))
                              : null}
                          </div>
                        )}
                      </div> */}
                      </div>
                      {errors.selectedLanguages && (
                        <span className="error-text">{t("positionTarget.validation.languagesRequired")}</span>
                      )}
                    </div>
                  </div>
                </div>
              </div>
              <div>
                <div className="form-group">
                  <label className="form-label">
                    {t("positionTarget.form.jobTitle.label")}
                    <span className="info-icon" data-tooltip={t("positionTarget.form.jobTitle.tooltip")} />
                    {!!jobToEdit && (
                      <i
                        className="fa fa-lock"
                        style={{ marginLeft: "6px", color: "#999", fontSize: "12px" }}
                        title="Title cannot be changed"
                      />
                    )}
                  </label>
                  <Controller
                    name="title"
                    control={control}
                    render={(renderProps) => (
                      <input
                        onChange={renderProps.onChange}
                        value={renderProps.value}
                        name="title"
                        type="text"
                        readOnly={!!jobToEdit}
                        placeholder={t("positionTarget.form.jobTitle.placeholder")}
                        className={`basic-input ${errors.title ? "error" : ""}${isReadOnly ? " readonly-input" : ""}`}
                        style={isReadOnly ? { backgroundColor: "#f5f5f5", cursor: "not-allowed" } : {}}
                      />
                    )}
                  />
                </div>
                <div className="form-row">
                  <div className="form-group half-width">
                    <label className="form-label">
                      {t("positionTarget.form.jobPostType.label")}
                      <span className="info-icon" data-tooltip={t("positionTarget.form.jobPostType.tooltip")} />
                    </label>
                    <div className={`radio-group ${errors.jobPostType ? "error" : ""}`}>
                      <Controller
                        name="jobPostType"
                        control={control}
                        render={(renderProps) => (
                          <div className="radio-options">
                            {jobPostTypesData &&
                              jobPostTypesData.map((jobType: any, index: number) => (
                                <label key={index} className="radio-option">
                                  <input
                                    type="radio"
                                    value={jobType.name}
                                    checked={renderProps.value === jobType.name}
                                    onChange={renderProps.onChange}
                                    disabled={isReadOnly}
                                  />
                                  <span>{jobType.name}</span>
                                </label>
                              ))}
                          </div>
                        )}
                      />
                    </div>
                    {errors.jobPostType && <span className="error-text">{errors.jobPostType.message}</span>}
                  </div>
                  <div className="form-group half-width">
                    <label className="form-label">
                      {t("positionTarget.form.educationLevel.label")}
                      <span className="info-icon" data-tooltip={t("positionTarget.form.educationLevel.tooltip")} />
                    </label>
                    <div className={`radio-group ${errors.educationLevel ? "error" : ""}`}>
                      <Controller
                        name="educationLevel"
                        control={control}
                        render={(renderProps) => (
                          <div className="radio-options">
                            {educationLevelsData?.map((level: EducationLevel, index: number) => (
                              <label key={level.id} className="radio-option">
                                <input
                                  type="radio"
                                  value={level.name}
                                  checked={renderProps.value === level.name}
                                  onChange={renderProps.onChange}
                                  disabled={isReadOnly}
                                />
                                <span>{level.name}</span>
                              </label>
                            ))}
                            <label className="radio-option">
                              <input
                                type="radio"
                                value=""
                                checked={renderProps.value === "" || renderProps.value === null}
                                onChange={renderProps.onChange}
                                disabled={isReadOnly}
                              />
                              <span>{t("positionTarget.form.educationLevel.any")}</span>
                            </label>
                          </div>
                        )}
                      />
                    </div>
                    {errors.educationLevel && <span className="error-text">{errors.educationLevel.message}</span>}
                  </div>
                </div>
                <div className="form-row">
                  <div className="form-group half-width">
                    <label className="form-label">
                      {t("positionTarget.form.workplace.label")}
                      <span className="info-icon" data-tooltip={t("positionTarget.form.workplace.tooltip")} />
                    </label>
                    <div className={`radio-group ${errors.workplace ? "error" : ""}`}>
                      <Controller
                        name="workplace"
                        control={control}
                        render={(renderProps) => (
                          <div className="radio-options">
                            {workplacesData?.map((workplace: any, index: number) => (
                              <label key={workplace.id} className="radio-option">
                                <input
                                  type="radio"
                                  value={workplace.name}
                                  checked={renderProps.value === workplace.name}
                                  onChange={renderProps.onChange}
                                  disabled={isReadOnly}
                                />
                                <span>{workplace.name}</span>
                              </label>
                            ))}
                          </div>
                        )}
                      />
                    </div>
                    {errors.workplace && <span className="error-text">{errors.workplace.message}</span>}
                  </div>
                </div>

                <div className="form-row">
                  <div className="form-group half-width">
                    <label className="form-label">Gender</label>
                    <div className="radio-options">
                      {[
                        { label: "Male", value: 1 },
                        { label: "Female", value: 2 },
                      ].map((g) => (
                        <label key={g.value} className="radio-option">
                          <input
                            type="radio"
                            name="gender"
                            value={g.value}
                            checked={selectedGenderId === g.value}
                            onChange={() => setSelectedGenderId(g.value)}
                            disabled={isReadOnly}
                          />
                          <span>{g.label}</span>
                        </label>
                      ))}
                    </div>
                  </div>
                </div>

                <div className="form-row">
                  <div className="form-group half-width">
                    <label className="form-label">Nationality</label>
                    {selectedNationalities.length > 0 && (
                      <div className="skills-tags" style={{ marginBottom: "8px" }}>
                        {selectedNationalities.map((name, i) => (
                          <span key={i} className="skill-tag">
                            {name}
                            {!isReadOnly && (
                              <button
                                type="button"
                                className="remove-skill"
                                onClick={() => handleNationalityRemove(name, selectedNationalityIds[i])}
                              >
                                ×
                              </button>
                            )}
                          </span>
                        ))}
                      </div>
                    )}
                    {!isReadOnly && (
                      <div style={{ pointerEvents: "auto" }}>
                        <ReactSearchAutocomplete
                          key={nationalitySelectKey}
                          items={hasNationalityInteracted ? nationalitySearchItems : []}
                          onSearch={(string) => {
                            nationalityCurrentSearchRef.current = string;
                            setHasNationalityInteracted(true);
                            if (nationalityDebounceRef.current) clearTimeout(nationalityDebounceRef.current);
                            nationalityDebounceRef.current = setTimeout(() => { getNationalitiesSearch(string); }, 300);
                          }}
                          onFocus={() => {
                            if (!hasNationalityInteracted) {
                              setHasNationalityInteracted(true);
                              getNationalitiesSearch();
                            }
                          }}
                          onSelect={handleNationalitySelect}
                          inputSearchString={nationalityInputSearch}
                          placeholder="Search nationality..."
                          showIcon={false}
                          showNoResults={false}
                          autoFocus={false}
                          styling={{
                            height: "45px",
                            border: "1px solid #d1d5db",
                            borderRadius: "6px",
                            backgroundColor: "white",
                            boxShadow: "none",
                            fontSize: "14px",
                            fontFamily: "inherit",
                            hoverBackgroundColor: "#f3f4f6",
                            color: "#374151",
                            placeholderColor: "#9ca3af",
                            clearIconMargin: "3px 8px 0 0",
                            zIndex: 3,
                          }}
                        />
                      </div>
                    )}
                  </div>
                  <div className="form-group half-width">
                    <label className="form-label">Industry</label>
                    <div style={{ pointerEvents: isReadOnly ? "none" : "auto", opacity: isReadOnly ? 0.7 : 1 }}>
                      <ReactSearchAutocomplete
                        key={industrySelectKey}
                        items={hasIndustryInteracted ? industryItems : []}
                        inputSearchString={industryInputSearch}
                        onSearch={(string) => {
                          industryCurrentSearchRef.current = string;
                          setHasIndustryInteracted(true);
                          if (industryDebounceRef.current) clearTimeout(industryDebounceRef.current);
                          industryDebounceRef.current = setTimeout(() => { loadIndustries(string); }, 300);
                        }}
                        onFocus={() => {
                          if (!hasIndustryInteracted) {
                            setHasIndustryInteracted(true);
                            loadIndustries();
                          }
                        }}
                        onSelect={handleIndustrySelect}
                        onClear={() => {
                          setSelectedIndustryId(null);
                          setSelectedIndustryName("");
                          setHasIndustryInteracted(false);
                          industryCurrentSearchRef.current = "";
                          setIndustryInputSearch("");
                          setIndustrySelectKey((k) => k + 1);
                        }}
                        placeholder="Search industry..."
                        showIcon={false}
                        showNoResults={false}
                        autoFocus={false}
                        styling={{
                          height: "45px",
                          border: "1px solid #d1d5db",
                          borderRadius: "6px",
                          backgroundColor: isReadOnly ? "#f9fafb" : "white",
                          boxShadow: "none",
                          fontSize: "14px",
                          fontFamily: "inherit",
                          hoverBackgroundColor: isReadOnly ? "#f9fafb" : "#f3f4f6",
                          color: isReadOnly ? "#9ca3af" : "#374151",
                          placeholderColor: "#9ca3af",
                          clearIconMargin: "3px 8px 0 0",
                          zIndex: 3,
                        }}
                      />
                    </div>
                  </div>
                </div>

                <div className="form-row">
                  <div className="form-group half-width">
                    <label className="form-label">
                      {t("positionTarget.form.country.label")}
                      <span className="info-icon" data-tooltip={t("positionTarget.form.country.tooltip")} />
                    </label>
                    <Controller
                      name="country"
                      control={control}
                      render={(renderProps) => (
                        <div className={`autocomplete-container ${errors.country ? "error" : ""}`}>
                          <div style={{ pointerEvents: isReadOnly ? "none" : "auto", opacity: isReadOnly ? 0.7 : 1 }}>
                            <ReactSearchAutocomplete
                              key={countrySelectKey}
                              items={hasCountryInteracted ? countries : []}
                              onSearch={(string, results) => {
                                setHasCountryInteracted(true);
                                if (string.length > 0) {
                                  getCountries(string);
                                } else {
                                  getCountries();
                                }
                              }}
                              onFocus={() => {
                                if (!hasCountryInteracted) {
                                  setHasCountryInteracted(true);
                                  getCountries();
                                }
                              }}
                              onSelect={(item) => handleCountrySelect(item, renderProps.onChange)}
                              onClear={() => {
                                renderProps.onChange("");
                                setSelectedCountryId("");
                                setSelectedCountryName("");
                                setSelectedCityId("");
                                setSelectedCityName("");
                                // Clear city field in form when no country is selected
                                setValue("jobLocation", "");
                                setCities([]);
                                setHasCountryInteracted(false);
                                setCountrySelectKey((k) => k + 1);
                                getCountries();
                              }}
                              placeholder={t("positionTarget.form.country.placeholder")}
                              inputSearchString={selectedCountryName || renderProps.value || ""}
                              showIcon={false}
                              showNoResults={false}
                              autoFocus={false}
                              styling={{
                                height: "45px",
                                border: "1px solid #d1d5db",
                                borderRadius: "6px",
                                backgroundColor: "white",
                                boxShadow: "none",
                                hoverBackgroundColor: "#f3f4f6",
                                color: "#374151",
                                fontSize: "14px",
                                fontFamily: "inherit",
                                iconColor: "#6b7280",
                                lineColor: "#e5e7eb",
                                placeholderColor: "#9ca3af",
                                clearIconMargin: "3px 8px 0 0",
                                zIndex: 2,
                              }}
                            />
                          </div>
                        </div>
                      )}
                    />
                    {errors.country && <span className="error-text">{errors.country.message}</span>}
                  </div>
                  <div className="form-group half-width">
                    <label className="form-label">
                      {t("positionTarget.form.city.label")}
                      <span className="info-icon" data-tooltip={t("positionTarget.form.city.tooltip")} />
                    </label>
                    <Controller
                      name="jobLocation"
                      control={control}
                      render={(renderProps) => (
                        <div className={`autocomplete-container ${errors.jobLocation ? "error" : ""}`}>
                          <div style={{ pointerEvents: isReadOnly ? "none" : "auto", opacity: isReadOnly ? 0.7 : 1 }}>
                            <ReactSearchAutocomplete
                              key={citySelectKey}
                              items={hasCityInteracted ? cities : []}
                              onSearch={(string, results) => {
                                setHasCityInteracted(true);
                                if (selectedCountryId) {
                                  if (string.length > 0) {
                                    getCities(selectedCountryId, string);
                                  } else {
                                    getCities(selectedCountryId);
                                  }
                                }
                              }}
                              onFocus={() => {
                                if (!hasCityInteracted && selectedCountryId) {
                                  setHasCityInteracted(true);
                                  getCities(selectedCountryId);
                                }
                              }}
                              onSelect={(item) => handleCitySelect(item, renderProps.onChange)}
                              onClear={() => {
                                renderProps.onChange("");
                                setSelectedCityId("");
                                setSelectedCityName("");
                                // Close dropdown by resetting interaction and remounting
                                setHasCityInteracted(false);
                                setCitySelectKey((k) => k + 1);
                                if (selectedCountryId) {
                                  getCities(selectedCountryId);
                                }
                              }}
                              placeholder={
                                !selectedCountryId
                                  ? t("positionTarget.form.city.selectCountryFirst")
                                  : t("positionTarget.form.city.placeholder")
                              }
                              inputSearchString={selectedCityName || renderProps.value || ""}
                              showIcon={false}
                              showNoResults={false}
                              autoFocus={false}
                              styling={{
                                height: "45px",
                                border: "1px solid #d1d5db",
                                borderRadius: "6px",
                                backgroundColor: selectedCountryId ? "white" : "#f9fafb",
                                boxShadow: "none",
                                hoverBackgroundColor: "#f3f4f6",
                                color: selectedCountryId ? "#374151" : "#9ca3af",
                                fontSize: "14px",
                                fontFamily: "inherit",
                                iconColor: "#6b7280",
                                lineColor: "#e5e7eb",
                                placeholderColor: "#9ca3af",
                                clearIconMargin: "3px 8px 0 0",
                                zIndex: 1,
                              }}
                            />
                          </div>
                        </div>
                      )}
                    />
                    {errors.jobLocation && <span className="error-text">{errors.jobLocation.message}</span>}
                  </div>
                </div>

                {/* Salary & Vacancies - Hidden for filter type jobs */}
                {!isFilterType && (
                  <div className="form-row">
                    <div className="form-group half-width  full-width-mobile">
                      <label className="form-label">
                        {t("positionTarget.form.salary.label")}
                        <span
                          className="info-icon"
                          data-tooltip-wide={t("positionTarget.form.salary.tooltip")}
                          data-tooltip={t("positionTarget.form.salary.tooltip")}
                        />
                      </label>

                      <div className="salary-month-fld" style={{ display: "flex", gap: "10px" }}>
                        <Controller
                          name="salary"
                          control={control}
                          render={(renderProps) => (
                            <div style={{ flex: 1, position: "relative" }}>
                              <input
                                onChange={(e) => {
                                  const value = e.target.value.replace(/\D/g, "").slice(0, 9);
                                  renderProps.onChange(value);
                                }}
                                value={renderProps.value}
                                name="salary"
                                type="text"
                                inputMode="numeric"
                                maxLength={9}
                                readOnly={isReadOnly}
                                className={`form-control ${errors.salary ? "error" : ""}`}
                                placeholder={t("positionTarget.form.salary.placeholder")}
                                style={{
                                  paddingLeft: "45px",
                                  ...(isReadOnly ? { backgroundColor: "#f5f5f5", cursor: "not-allowed" } : {}),
                                }}
                              />
                              <span
                                style={{
                                  position: "absolute",
                                  left: "12px",
                                  top: "50%",
                                  transform: "translateY(-50%)",
                                  color: "#64748b",
                                  fontSize: "14px",
                                  fontWeight: "500",
                                }}
                              >
                                {selectedCurrency}
                              </span>
                            </div>
                          )}
                        />

                        <Controller
                          name="salaryPeriod"
                          control={control}
                          render={(renderProps) => (
                            <select
                              onChange={renderProps.onChange}
                              value={renderProps.value}
                              name="salaryPeriod"
                              disabled={isReadOnly}
                              className={`basic-select ${errors.salaryPeriod ? "error" : ""}`}
                              style={{ flex: 1 }}
                            >
                              <option value="daily">{t("positionTarget.form.salary.periods.daily")}</option>
                              <option value="weekly">{t("positionTarget.form.salary.periods.weekly")}</option>
                              <option value="monthly">{t("positionTarget.form.salary.periods.monthly")}</option>
                              <option value="yearly">{t("positionTarget.form.salary.periods.yearly")}</option>
                            </select>
                          )}
                        />
                      </div>

                      {errors.salary && <span className="error-text">{errors.salary.message}</span>}
                      {errors.salaryPeriod && <span className="error-text">{errors.salaryPeriod.message}</span>}

                      <div className="currency-change" style={{ marginTop: "10px" }}>
                        <button
                          type="button"
                          className="currency-btn"
                          disabled={isReadOnly}
                          onClick={() => {
                            setShowCurrencyModal(true);
                          }}
                        >
                          {selectedCurrency} <span>{t("positionTarget.form.salary.changeCurrency")}</span>
                        </button>
                      </div>

                      {/* Hide Salary Checkbox */}
                      <div className="checkbox-container" style={{ marginTop: "10px" }}>
                        <label
                          className="checkbox-label"
                          style={{ display: "flex", alignItems: "center", cursor: "pointer" }}
                        >
                          <input
                            type="checkbox"
                            checked={hideSalary}
                            onChange={(e) => setHideSalary(e.target.checked)}
                            disabled={isReadOnly}
                            style={{ marginRight: "8px", cursor: isReadOnly ? "not-allowed" : "pointer" }}
                          />
                          <span style={{ fontSize: "14px", color: "#374151" }}>
                            {t("positionTarget.form.salary.hideSalary")}
                          </span>
                        </label>
                      </div>
                    </div>

                    <div className="form-group half-width">
                      <label className="form-label">
                        {t("positionTarget.form.vacancies.label")}
                        <span className="info-icon" data-tooltip={t("positionTarget.form.vacancies.tooltip")} />
                      </label>

                      <Controller
                        name="vacancies"
                        control={control}
                        render={(renderProps) => (
                          <div className="number-input-container">
                            <button
                              type="button"
                              className="number-btn"
                              disabled={isReadOnly}
                              onClick={() => renderProps.onChange(Math.max(1, renderProps.value - 1))}
                            >
                              −
                            </button>
                            <input
                              onChange={(e) => renderProps.onChange(parseInt(e.target.value) || 1)}
                              value={renderProps.value}
                              name="vacancies"
                              type="number"
                              min="1"
                              readOnly={isReadOnly}
                              className={`number-input ${errors.vacancies ? "error" : ""}`}
                            />
                            <button
                              type="button"
                              className="number-btn"
                              disabled={isReadOnly}
                              onClick={() => renderProps.onChange(renderProps.value + 1)}
                            >
                              +
                            </button>
                          </div>
                        )}
                      />
                      {errors.vacancies && <span className="error-text">{errors.vacancies.message}</span>}
                    </div>
                  </div>
                )}

                <div className="form-group">
                  <label className="form-label">
                    {t("positionTarget.form.jobDescription.label")}
                    <span
                      className="info-icon"
                      data-tooltip-wide={t("positionTarget.form.jobDescription.tooltip")}
                      data-tooltip={t("positionTarget.form.jobDescription.tooltip")}
                    />
                  </label>
                  <div className="basic-textarea-container">
                    <Controller
                      name="jobDescription"
                      control={control}
                      render={(renderProps) => (
                        <div className={`rich-text-editor ${errors.jobDescription ? "error" : ""}`}>
                          <ReactQuill
                            theme="snow"
                            value={renderProps.value || ""}
                            onChange={(content) => {
                              // Store the HTML content; validation will strip tags for length checks
                              renderProps.onChange(content);
                            }}
                            readOnly={isReadOnly}
                            placeholder={jobDescriptionPlaceholder}
                            modules={{
                              toolbar: false,
                            }}
                            formats={["header", "bold", "italic", "underline", "list", "bullet", "link"]}
                          />
                        </div>
                      )}
                    />
                    <div className="ai-language-dropdown-container">
                      <div
                        className="ai-enhance-wrapper"
                        onClick={isReadOnly ? undefined : handleOpenAiLanguageDropdown}
                      >
                        <button
                          type="button"
                          className="ai-generate-btn"
                          disabled={isReadOnly || isEnhancing || isGeneratingJobDescription}
                          title={t("positionTarget.form.jobDescription.enhanceTitle")}
                        />
                        <span style={{ fontSize: "14px", color: isEnhancing ? "#9ca3af" : "#374151", fontWeight: 500 }}>
                          {isEnhancing
                            ? t("positionTarget.aiModal.enhancing")
                            : watch("jobDescription") && watch("jobDescription").trim()
                              ? t("positionTarget.messages.regenerateWithAI")
                              : t("positionTarget.messages.generateWithAI")}
                        </span>
                      </div>

                      {/* Language Dropdown */}
                      {showAiLanguageDropdown && (
                        <div className="ai-language-dropdown">
                          <div className="lang-wrap">
                            {[
                              { code: "EN", name: "English" },
                              { code: "AR", name: "Arabic" },
                              { code: "FR", name: "Français" },
                              { code: "ES", name: "Español" },
                              { code: "HI", name: "हिन्दी" },
                            ].map((lang) => (
                              <div
                                key={lang.code}
                                onClick={() => handleAiLanguageSelect(lang.code)}
                                className="lang-list"
                              >
                                <div className="lang-list-inner">
                                  <span className="lang-code">{lang.code}</span>
                                </div>
                              </div>
                            ))}
                          </div>
                        </div>
                      )}
                    </div>
                  </div>
                  {errors.jobDescription && <span className="error-text">{errors.jobDescription.message}</span>}
                </div>

                {/* Skills Field */}
                <div className="form-group">
                  <div className="skills-container">
                    <label className="form-label">
                      {t("positionTarget.form.skills.label")}
                      <span
                        className="info-icon"
                        data-tooltip-wide={t("positionTarget.form.skills.tooltip")}
                        data-tooltip={t("positionTarget.form.skills.tooltip")}
                      />
                    </label>
                    {/* Skills Tags */}
                    {selectedSkills.length > 0 ? (
                      <div className="skills-tags">
                        {selectedSkills.map((skill, index) => (
                          <span key={index} className="skill-tag">
                            {skill}
                            {!isReadOnly && (
                              <button type="button" className="remove-skill" onClick={() => handleSkillRemove(skill)}>
                                ×
                              </button>
                            )}
                          </span>
                        ))}
                      </div>
                    ) : null}
                    <div className="d-flex gap-2 wrap-job-skills">
                      {/* Skills Modal Button */}
                      <div className="skills-dropdown-container">
                        <button
                          type="button"
                          className="btn-cancel"
                          disabled={isReadOnly}
                          onClick={() => setShowSkillModal(true)}
                        >
                          <i className="fa fa-plus" aria-hidden="true"></i>
                          {t("positionTarget.form.skills.addSkills")}
                        </button>
                      </div>

                      {/* AI Skills Generator Section */}
                      {aiGeneratedSkills.length > 0 && (
                        <div className="ai-skills-generator-section">
                          <button
                            type="button"
                            className="ai-skills-btn"
                            onClick={() => setShowAiSkillsSection(!showAiSkillsSection)}
                          >
                            <span className="ai-icon">✨</span>
                            {t("positionTarget.form.skills.aiGenerator")}
                          </button>
                        </div>
                      )}
                    </div>
                    {showAiSkillsSection && (
                      <div className="ai-skills-info-container">
                        {aiGeneratedSkills.length > 0 && (
                          <div>
                            <div className="skills-tags ai-generated-skill">
                              {aiGeneratedSkills.map((skill, index) => (
                                <span key={index} className="skill-tag ai-skill">
                                  {skill}
                                  <button
                                    type="button"
                                    className="add-skill d-none"
                                    onClick={() => handleAiSkillAdd(skill)}
                                    title="Add to selected skills"
                                  >
                                    {" "}
                                    +
                                  </button>
                                </span>
                              ))}
                            </div>
                            <button className="apply-suggestions-btn d-none">
                              <span className="plus-icon">+</span> {t("positionTarget.form.skills.applySuggestions")}
                            </button>
                          </div>
                        )}
                      </div>
                    )}
                  </div>
                </div>
              </div>
            </div>
          </div>
        </form>
      </div>

      {/* Currency Selection Modal */}
      {showCurrencyModal && (
        <CurrencySelectionModal
          isOpen={showCurrencyModal}
          onClose={() => setShowCurrencyModal(false)}
          currenciesData={currenciesData}
          selectedCurrency={selectedCurrency}
          onCurrencySelect={(currency, currencySymbol) => {
            setSelectedCurrency(currency);
            setSelectedCurrencySymbol(currencySymbol);
          }}
        />
      )}

      {/* Skill Selection Modal */}
      {showSkillModal && (
        <SkillSelectionModal
          isOpen={showSkillModal}
          onClose={() => setShowSkillModal(false)}
          skillsData={skillsData}
          onSkillSelect={handleSkillAdd}
          selectedSkills={selectedSkills}
        />
      )}
    </div>
  );
}
